import { useMutation } from "@tanstack/react-query";
import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { FinancialDocument } from "@common/types/financialDocument";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import {
    detachFinancialTransactionMatch,
    matchTransfer,
    unmatchTransfer,
    updateTransaction,
} from "@lib/transactions";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { invalidateTransactionDetailsQueries } from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import { removeJournalEntryQueries } from "@components/accounting/journalEntryDrawer/journalEntryQuery";
import { createLinkFinancialDocumentToFinancialTransaction } from "@lib/financialDocument";
import { invalidateTransactionsQueries } from "@components/transactions/useTransactionsQuery";
import { invalidateTransactionsForTransferMatchingQueries } from "@components/transactions/TransferMatching/useTransactionsForTransferMatching";

interface UpdateTransactionOptions {
    transaction: TransactionListItem;
    update: UpdateTransactionDto;
}

export function useUpdateTransactionMutation() {
    const { invalidate: invalidateCreditsUsed } = useBillingStatus();

    return useMutation({
        mutationFn: ({ transaction, update }: UpdateTransactionOptions) =>
            updateTransaction(transaction, update),
        onSuccess: async (_data, { update }) => {
            // The JE drawer query uses refetchOnMount: false, so invalidate
            // would leave stale grouped-split lines after a trx edit.
            removeJournalEntryQueries();

            // Class-only edits cannot affect usage, so skip the billing
            // refetch. `loosingAccessConfirmed` is always merged into the
            // update by saveTransaction, so it must be in the allowed set.
            const isClassOnlyUpdate = Object.keys(update).every(
                (key) => key === "classIds" || key === "loosingAccessConfirmed",
            );
            if (!isClassOnlyUpdate) {
                void invalidateCreditsUsed();
            }
        },
    });
}

export function useDetachTransactionMatchMutation(
    transactionId: number,
    matchId: number | undefined,
) {
    const { invalidate: invalidateCreditsUsed } = useBillingStatus();

    return useMutation({
        mutationFn: () => {
            if (matchId === undefined) {
                throw new Error("No transaction match to detach");
            }

            return detachFinancialTransactionMatch(transactionId, matchId);
        },
        onSuccess: async () => {
            await invalidateCreditsUsed();
            await invalidateTransactionDetailsQueries();
            await invalidateTransactionsQueries();
        },
    });
}

export function useCreateLinkToFinancialDocument(
    financialTransaction: TransactionListItem,
    financialDocument: FinancialDocument,
    onLinked?: () => Promise<unknown> | void,
) {
    return useMutation({
        mutationFn: () =>
            createLinkFinancialDocumentToFinancialTransaction({
                financialDocumentId: financialDocument.id,
                financialTransactionId: financialTransaction.id,
            }),

        async onSuccess() {
            await invalidateTransactionDetailsQueries();
            await onLinked?.();
        },
    });
}

interface UnmatchTransferParams {
    financialTransaction: Pick<TransactionListItem, "id">;
    onSuccess: (transaction: Pick<TransactionListItem, "id">) => void;
}

export function useUnmatchTransfer({
    financialTransaction,
    onSuccess,
}: UnmatchTransferParams) {
    return useMutation({
        mutationFn: () => unmatchTransfer(financialTransaction.id),
        onSuccess: async () => {
            await Promise.all([
                invalidateTransactionDetailsQueries(),
                invalidateTransactionsQueries(),
                invalidateTransactionsForTransferMatchingQueries(),
            ]);
            onSuccess(financialTransaction);
        },
        mutationKey: ["unmatchTransfer", financialTransaction],
    });
}

interface MatchTransferOnSuccessParams {
    originTransaction: TransactionListItem;
    targetTransaction: TransactionListItem;
}

export function useMatchTransfer({
    originTransaction,
    targetTransaction,
    onSuccess,
}: {
    originTransaction: TransactionListItem;
    targetTransaction: TransactionListItem;
    onSuccess: ({
        originTransaction,
        targetTransaction,
    }: MatchTransferOnSuccessParams) => void;
}) {
    return useMutation({
        mutationFn: () =>
            matchTransfer(originTransaction.id, targetTransaction.id),
        // NOTE: server response is compact ({ id } only) per
        // matchTransferResponseSchema, so we cannot merge it into caches.
        // Invalidate affected queries instead so they refetch fully
        // hydrated transactions (including Date types).
        onSuccess: async () => {
            await Promise.all([
                invalidateTransactionDetailsQueries(),
                invalidateTransactionsQueries(),
                invalidateTransactionsForTransferMatchingQueries(),
            ]);
            onSuccess({ originTransaction, targetTransaction });
        },
        mutationKey: [
            "matchTransfer",
            originTransaction.id,
            targetTransaction.id,
        ],
    });
}
